import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, Play, Calendar, Clock } from 'lucide-react'
import { Anime } from '@/types/anime'

interface AnimeCardProps {
  anime: Anime
  size?: 'sm' | 'md' | 'lg'
}

export function AnimeCard({ anime, size = 'md' }: AnimeCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)

  const sizeClasses = {
    sm: 'w-32 h-44',
    md: 'w-40 h-56',
    lg: 'w-48 h-64'
  }

  return (
    <Link to={`/anime/${anime.id}`}>
      <div className="anime-card group relative bg-dark-200 rounded-lg overflow-hidden shadow-lg">
        {/* Cover Image */}
        <div className={`relative ${sizeClasses[size]} overflow-hidden`}>
          <img
            src={anime.coverImage.large}
            alt={anime.title.romaji}
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Loading Skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-700 shimmer" />
          )}

          {/* Hover Overlay */}
          <div className="anime-card-overlay flex items-center justify-center">
            <div className="bg-blue-600 p-3 rounded-full transform scale-75 group-hover:scale-100 transition-transform duration-300">
              <Play className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Score Badge */}
          {anime.averageScore && (
            <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm px-2 py-1 rounded-lg">
              <div className="flex items-center space-x-1 text-xs">
                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-white font-medium">{anime.averageScore}%</span>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              anime.status === 'RELEASING' 
                ? 'bg-green-600 text-white' 
                : anime.status === 'FINISHED'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-600 text-white'
            }`}>
              {anime.status === 'RELEASING' ? 'ONGOING' : 'COMPLETED'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
            {anime.title.english || anime.title.romaji}
          </h3>

          {/* Meta Info */}
          <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
            {anime.seasonYear && (
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{anime.seasonYear}</span>
              </div>
            )}
            {anime.episodes && (
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{anime.episodes} eps</span>
              </div>
            )}
          </div>

          {/* Genres */}
          <div className="flex flex-wrap gap-1">
            {anime.genres.slice(0, 2).map((genre) => (
              <span
                key={genre}
                className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded"
              >
                {genre}
              </span>
            ))}
          </div>

          {/* Studio */}
          {anime.studios.nodes.length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {anime.studios.nodes[0].name}
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}
